import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { irTimeline, investmentPoints, irDownloads } from "../../mocks/ir";
import JsonLd from "../../components/base/JsonLd";

const irJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "투자자 IR - 에너버즈(EnerVerze)",
  "description": "에너버즈는 AI와 에너지의 융합으로 새로운 가치를 창출합니다. 투자자 여러분의 관심과 지원을 통해 글로벌 에너지 혁신을 이끌어가겠습니다.",
  "url": "https://enerverze.com/ir",
  "inLanguage": "ko-KR",
  "isPartOf": {
    "@type": "WebSite",
    "name": "에너버즈(EnerVerze)",
    "url": "https://enerverze.com/"
  },
  "about": {
    "@type": "Thing",
    "name": "Investor Relations",
    "description": "에너버즈 기업 성장 전략, 투자 포인트, IR 자료"
  }
};

export default function IRPage() {
  return (
    <div className="min-h-screen bg-enerverze-bg">
      <JsonLd data={irJsonLd} />
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20financial%20growth%20and%20investment%20concept%20with%20upward%20trending%20charts%20and%20glowing%20coins%20on%20dark%20slate%20background%20neon%20cyan%20and%20green%20accents%20futuristic%20business%20aesthetic%20high%20quality%203D%20render%20minimalist%20design&width=1920&height=600&seq=ir-hero&orientation=landscape"
              alt="IR 배경"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-enerverze-bg/60 via-enerverze-bg/80 to-enerverze-bg"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <p className="text-sm text-neon-cyan mb-3">Investor Relations</p>
            <h1 className="text-4xl md:text-6xl font-bold text-enerverze-text leading-tight mb-4">
              투자자 <span className="text-neon-cyan">IR</span>
            </h1>
            <p className="text-base md:text-lg text-enerverze-muted max-w-2xl">
              에너버즈는 AI와 에너지의 융합으로 새로운 가치를 창출합니다. 
              투자자 여러분의 관심과 지원을 통해 글로벌 에너지 혁신을 이끌어가겠습니다.
            </p>
          </div>
        </section>

        {/* Growth Timeline */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                기업 성장 전략
              </h2>
              <p className="text-sm text-enerverze-muted">에너버즈의 단계별 성장 로드맵</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-enerverze-border md:-translate-x-px"></div>
              <div className="space-y-12">
                {irTimeline.map((item, idx) => (
                  <div
                    key={idx}
                    className={`relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-0 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                      <div className="p-6 rounded-2xl bg-enerverze-card border border-enerverze-border hover:border-neon-cyan transition-all">
                        <p className="text-3xl font-bold text-neon-cyan mb-2">{item.year}</p>
                        <h3 className="text-lg font-semibold text-enerverze-text mb-2">{item.title}</h3>
                        <p className="text-sm text-enerverze-muted">{item.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-neon-cyan border-2 border-enerverze-bg md:-translate-x-1.5 translate-x-0.5 md:translate-x-0 z-10 mt-6 md:mt-0"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Investment Points */}
        <section className="py-16 md:py-24 bg-enerverze-card/30 border-y border-enerverze-border">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                투자 <span className="text-neon-cyan">포인트</span>
              </h2>
              <p className="text-sm text-enerverze-muted">에너버즈에 투자해야 하는 4가지 이유</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {investmentPoints.map((point, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-enerverze-card border border-enerverze-border hover:border-neon-cyan transition-all group"
                >
                  <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-neon-cyan/10 border border-neon-cyan/30 mb-5 group-hover:bg-neon-cyan/20 transition-colors">
                    <i className={`${point.icon} text-2xl text-neon-cyan`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-enerverze-text mb-3">{point.title}</h3>
                  <p className="text-sm text-enerverze-muted leading-relaxed">{point.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* IR Downloads */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                IR 자료
              </h2>
              <p className="text-sm text-enerverze-muted">투자 판단에 필요한 공식 자료를 다운로드하세요</p>
            </div>

            <div className="space-y-4 max-w-3xl">
              {irDownloads.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-6 rounded-2xl bg-enerverze-card border border-enerverze-border hover:border-neon-cyan transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-neon-cyan/10 border border-neon-cyan/30 flex-shrink-0">
                      <i className="ri-file-pdf-line text-xl text-neon-cyan"></i>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-enerverze-text mb-1">{item.title}</h3>
                      <p className="text-sm text-enerverze-muted">{item.desc}</p>
                    </div>
                  </div>
                  <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-neon-cyan/10 border border-neon-cyan/30 text-neon-cyan text-sm font-medium hover:bg-neon-cyan hover:text-enerverze-bg transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-download-line"></i>
                    <span>{item.format}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}